import { useState, useEffect } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { adminApi, errText } from "@/lib/api";

const EFFECT_TYPES = ["golden_sweep", "diamond_shatter", "royal_cinematic", "neon_storm", "fire_wave", "butterfly_bloom", "galaxy_warp"];

export default function AdminEffects() {
  const [effects, setEffects] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", emoji: "✨", price: 1000, effect_type: "neon_storm" });

  const load = () => adminApi.get("/admin/entry-effects").then(({ data }) => setEffects(data.effects)).catch(() => setEffects([]));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await adminApi.post("/admin/entry-effects", { ...form, price: Number(form.price) });
      toast.success("Entry effect created — live in the Store");
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(errText(err));
    }
  };

  const toggle = async (eff) => {
    try {
      await adminApi.put(`/admin/entry-effects/${eff.id}`, { active: !eff.active });
      setEffects((es) => es.map((x) => (x.id === eff.id ? { ...x, active: !x.active } : x)));
      toast.success(`${eff.name} ${!eff.active ? "activated" : "deactivated"}`);
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const updatePrice = async (eff, price) => {
    if (!price || Number(price) === eff.price) return;
    try {
      await adminApi.put(`/admin/entry-effects/${eff.id}`, { price: Number(price) });
      toast.success("Price updated");
      load();
    } catch (e) {
      toast.error(errText(e));
    }
  };

  return (
    <div data-testid="admin-effects">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-extrabold font-display">Entry Effects Store</h1>
        <button data-testid="new-effect-button" onClick={() => setShowForm(!showForm)}
          className="gradient-primary text-white text-xs font-bold px-4 py-2 rounded-full flex items-center gap-1.5"><Plus size={14} /> New Effect</button>
      </div>
      <p className="text-zinc-500 text-xs -mt-4 mb-5">Purchasable cosmetic entry animations users can buy with coins and equip.</p>

      {showForm && (
        <form onSubmit={create} className="glass rounded-2xl p-4 mb-4 flex flex-wrap items-end gap-3">
          <div><label className="text-[10px] text-zinc-500 font-semibold block mb-1">Name</label>
            <input data-testid="effect-name-input" required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              className="w-40 bg-[#1A1A24] border border-white/10 rounded-lg px-2.5 py-2 text-sm outline-none" /></div>
          <div><label className="text-[10px] text-zinc-500 font-semibold block mb-1">Emoji</label>
            <input data-testid="effect-emoji-input" value={form.emoji} onChange={(e) => setForm((f) => ({ ...f, emoji: e.target.value }))}
              className="w-16 bg-[#1A1A24] border border-white/10 rounded-lg px-2 py-2 text-sm outline-none text-center" /></div>
          <div><label className="text-[10px] text-zinc-500 font-semibold block mb-1">Price (coins)</label>
            <input data-testid="effect-price-input" type="number" min={1} value={form.price} onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))}
              className="w-28 bg-[#1A1A24] border border-white/10 rounded-lg px-2 py-2 text-sm outline-none" /></div>
          <div><label className="text-[10px] text-zinc-500 font-semibold block mb-1">Animation</label>
            <select data-testid="effect-type-select" value={form.effect_type} onChange={(e) => setForm((f) => ({ ...f, effect_type: e.target.value }))}
              className="bg-[#1A1A24] border border-white/10 rounded-lg px-2 py-2 text-sm outline-none">
              {EFFECT_TYPES.map((t) => <option key={t}>{t}</option>)}
            </select></div>
          <button data-testid="effect-create-submit" className="gradient-primary text-white text-xs font-bold px-5 py-2.5 rounded-xl">Create</button>
        </form>
      )}

      <div className="glass rounded-2xl overflow-hidden">
        <table className="w-full text-xs">
          <thead><tr className="text-zinc-500 text-left border-b border-white/10"><th className="p-3">Effect</th><th>Animation</th><th>Price</th><th>Status</th></tr></thead>
          <tbody>
            {effects?.map((e) => (
              <tr key={e.id} className="border-b border-white/5" data-testid={`effect-row-${e.id}`}>
                <td className="p-3 flex items-center gap-2"><span className="text-xl">{e.emoji}</span><span className="text-white font-semibold">{e.name}</span></td>
                <td className="text-zinc-400">{e.effect_type}</td>
                <td>
                  <input type="number" defaultValue={e.price} data-testid={`effect-price-edit-${e.id}`} onBlur={(ev) => updatePrice(e, ev.target.value)}
                    className="w-24 bg-[#1A1A24] border border-white/10 rounded-lg px-2 py-1 text-amber-400 font-bold outline-none focus:border-violet-500" />
                </td>
                <td className="pr-3">
                  <button data-testid={`effect-toggle-${e.id}`} onClick={() => toggle(e)}
                    className={`px-2.5 py-1 rounded-lg font-semibold ${e.active ? "bg-emerald-500/20 text-emerald-400" : "bg-rose-500/20 text-rose-400"}`}>
                    {e.active ? "Active" : "Inactive"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
